import ImportWizardContextProvider from "components/importer/ImportWizardContext";
import {
  CreateEntityParams,
  IdentifyDuplicatesParams,
  Property,
  PropertyType,
  ImportWizardActionHandler,
  EntityTypes,
  TraxeroActionResponse,
  FailedImport,
  Tenant,
} from "types";
import {
  getBlankProperty,
  logError,
  capitalize,
  getShortFormattedAddress,
  getBlankContact,
  getCountryFromStateCode,
} from "utils";
import { PropertyField, propertyFieldMetadata } from "./propertyFieldMetadata";
import { useTranslation } from "react-i18next";
import { ActionFunction, useLocation } from "react-router";
import {
  createProperty,
  importProperties,
  saveImportColumnMappings,
} from "api";
import ImportWizard from "components/importer/ImportWizard";
import { useFetcherKey } from "hooks/useFetcherKey";
import { useCallback, useMemo, useState, useContext } from "react";
import { MRT_ColumnDef } from "material-react-table";
import PropertyCorrectionForm from "./PropertyCorrectionForm";
import TenantSelectWidget from "./TenantSelectWidget";
import { AppContext } from "context/appContext";

const PropertyImportWizard = () => {
  const { t } = useTranslation();
  const { fetcherKey } = useFetcherKey();
  const location = useLocation();
  const { state } = useContext(AppContext);
  const [tenant, setTenant] = useState<Tenant>(state.tenant);

  const identifyDuplicateProperties = useCallback(
    ({ columnMappings, data }: IdentifyDuplicatesParams) => {
      const duplicates: number[] = [];
      const addressFields = [
        "property.location.street",
        "property.location.city",
        "property.location.stateCode",
        "property.location.postalCode",
      ];

      // Check if we have all required address fields mapped
      const hasRequiredAddressFields = addressFields.every(
        (field) =>
          columnMappings[field] && columnMappings[field] !== "Leave Blank",
      );

      if (!hasRequiredAddressFields) {
        return duplicates;
      }

      const addressSet: Set<string> = new Set();

      data.forEach((row, index) => {
        // Create a normalized address key by combining all address components
        const addressComponents = addressFields.map((field) => {
          const value = row[columnMappings[field]] || "";
          return value.toLowerCase().trim();
        });

        // Only include unit in the key if it exists
        const addressKey = addressComponents
          .filter((component) => component.length > 0)
          .join("|");

        if (addressSet.has(addressKey)) {
          duplicates.push(index);
        } else {
          addressSet.add(addressKey);
        }
      });

      return duplicates;
    },
    [],
  );

  function createProperty({ row, mappings }: CreateEntityParams): Property {
    const blankProperty = getBlankProperty(tenant.id);
    const blankContact = getBlankContact(tenant.id);
    const property = { ...blankProperty };
    const contact = { ...blankContact };

    Object.entries(mappings).forEach(([propertyField, csvHeader]) => {
      if (csvHeader === "Leave Blank") {
        return;
      }
      const value = row[csvHeader] || "";
      switch (propertyField as PropertyField) {
        case "property.name":
          if (value.trim() === "") {
            property.name = "Unnamed Property";
          } else {
            property.name = value.trim();
          }
          break;
        case "property.description":
          property.description = value.trim();
          break;
        case "property.type":
          property.type = value.toLowerCase() as PropertyType;
          break;
        case "property.externalID":
          property.externalID = value.trim();
          break;
        case "property.contact.name":
          if (value.trim() === "") {
            contact.name = "Unknown Contact";
          } else {
            contact.name = value.trim();
          }
          break;
        case "property.contact.primaryEmail":
          contact.primaryEmail = value.toLowerCase().trim();
          break;
        case "property.contact.primaryPhone":
          if (value.trim() === "") {
            contact.primaryPhone = "";
          } else {
            contact.primaryPhone = value.replace(/\D/g, "");
          }
          break;
        case "property.location.street":
          property.location.street = value.trim();
          break;
        case "property.location.city":
          property.location.city = value.trim();
          break;
        case "property.location.stateCode":
          property.location.stateCode = value.toUpperCase();
          property.location.country = getCountryFromStateCode(value);
          break;
        case "property.location.postalCode":
          property.location.postalCode = value.trim();
          break;
        case "property.data.Metadata.BillingCustomerKey":
          if (!property.data.Metadata) {
            property.data.Metadata = {};
          }
          property.data.Metadata.billingCustomerKey = value.trim();
          break;
        case "property.data.settings.notes":
          property.data.settings.notes = value.trim();
          break;
      }
    });
    property.contacts = [contact];
    return property;
  }

  const columns = useMemo<MRT_ColumnDef<Property>[]>(
    () => [
      {
        header: t("property.name", { ns: "entities" }),
        accessorKey: "name",
      },
      {
        header: t("property.description", { ns: "entities" }),
        accessorKey: "description",
      },
      {
        header: t("property.location.label", { ns: "entities" }),
        id: "address",
        accessorFn: (row) => getShortFormattedAddress(row.location),
      },
      {
        header: t("property.type", { ns: "entities" }),
        id: "type",
        accessorFn: (row) => capitalize(row.type),
      },
    ],
    [t],
  );

  return (
    <ImportWizardContextProvider<Property, PropertyField>
      createEntity={createProperty}
      identifyDuplicates={identifyDuplicateProperties}
      metadata={propertyFieldMetadata}
      entityName={t("property.label", { ns: "entities" })}
      defaultAddress={{}} // unused in property import
    >
      <ImportWizard<Property>
        fetcherKey={fetcherKey}
        actionRoute={location.pathname}
        previewColumns={columns}
        CorrectionForm={PropertyCorrectionForm}
        maxHeight={`calc(100dvh - 464px)`}
        UploadStepRightComponent={
          <TenantSelectWidget tenant={tenant} setTenant={setTenant} />
        }
        _tenant={tenant}
      />
    </ImportWizardContextProvider>
  );
};

export const propertyImportWizardAction: ActionFunction = async ({
  request,
}): Promise<
  TraxeroActionResponse<ImportWizardActionHandler, FailedImport<Property>[]>
> => {
  const { body, handler, permitTypeID, systemID, tenantID } = await request.json();


  let res: Response;
  try {
    switch (handler) {
      case ImportWizardActionHandler.SAVE_COLUMN_MAPPINGS:
        // TODO: address this route, this will not work
        res = await saveImportColumnMappings({
          tenantID,
          entityID: tenantID,
          entityType: EntityTypes.Tenant,
          permitTypeID,
          body,
        });
        break;
      case ImportWizardActionHandler.SUBMIT_ENTITY_DATA:
        res = await importProperties({
          tenantID,
          systemID,
          body,
        });
        break;
      case ImportWizardActionHandler.CREATE_ENTITY:
        res = await createProperty({
          tenantID,
          body,
        });
        break;
      default:
        res = new Response(null, {
          status: 500,
          statusText: "unknown handler",
        });
    }

    if (
      res.status === 200 &&
      handler === ImportWizardActionHandler.SUBMIT_ENTITY_DATA
    ) {
      const failedEntities = await res.json();
      return {
        handler,
        status: res.status,
        statusText: res.statusText,
        body: failedEntities,
      };
    }
    return {
      handler,
      status: res.status,
      statusText: res.statusText,
    };
  } catch (error) {
    logError(error as Error);
    return {
      handler,
      status: 500,
      statusText: "Internal Server Error",
    };
  }
};

export default PropertyImportWizard;
